// /reset-password — landing page for the URL in the password-reset email.
//
// Two-step UX:
//   1. Page mounts with ?token=...; we hold it locally without verifying
//      (the only way to verify a reset token is to consume it).
//   2. User types a new password + confirm; submit POSTs {token, new_password}.
//   3. On success: redirect to /login with a small banner. The reset
//      revoked every active session, so they have to sign back in.

import { useState } from "react";
import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { Logo } from "../components/Logo";
import { PasswordStrength } from "../components/PasswordStrength";
import { useI18n } from "../lib/i18n";
import { ApiError, auth } from "../lib/api";

function ResetPassword() {
  const { t } = useI18n();
  const search = useSearch({ from: "/reset-password" }) as { token?: string };
  const nav = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const token = search.token?.trim() ?? "";
  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = !!token && password.length >= 8 && password === confirm;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setErr(null);
    try {
      await auth.resetPassword({ token, new_password: password });
      void nav({ to: "/login", search: { reset: "1" }, replace: true });
    } catch (e) {
      if (e instanceof ApiError && e.status === 410) {
        setErr(t("resetPassword.expired"));
      } else if (e instanceof ApiError && e.status === 400) {
        setErr((e.body as { error?: string })?.error || t("common.errorGeneric"));
      } else {
        setErr(t("common.errorGeneric"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <div className="min-h-full flex items-center justify-center px-4">
        <Card className="w-full max-w-md p-8 flex flex-col gap-5">
          <Logo size="sm" />
          <h1 className="font-display text-2xl font-semibold text-error">{t("resetPassword.missingToken")}</h1>
          <p className="text-secondary text-sm">{t("resetPassword.missingTokenBody")}</p>
          <Link to="/forgot-password"><Button className="w-full">{t("resetPassword.requestNew")}</Button></Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-full flex items-center justify-center px-4">
      <Card className="w-full max-w-md p-8 flex flex-col gap-5">
        <Link to="/login" className="ring-nexus rounded self-start"><Logo size="sm" /></Link>
        <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
          <h1 className="font-display text-2xl font-semibold">{t("resetPassword.title")}</h1>
          <p className="text-secondary text-sm">{t("resetPassword.subtitle")}</p>
          <Input
            label={t("resetPassword.newPassword")}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            errorText={tooShort ? t("resetPassword.tooShort") : null}
            autoComplete="new-password"
            required
            autoFocus
          />
          <PasswordStrength
            password={password}
            labels={{
              weak: t("auth.signup.strength.weak"),
              fair: t("auth.signup.strength.fair"),
              good: t("auth.signup.strength.good"),
              strong: t("auth.signup.strength.strong"),
            }}
          />
          <Input
            label={t("resetPassword.confirm")}
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            errorText={mismatch ? t("resetPassword.mismatch") : null}
            autoComplete="new-password"
            required
          />
          {err ? <p className="text-error text-sm" role="alert">{err}</p> : null}
          <Button type="submit" loading={submitting} disabled={!canSubmit}>
            {t("resetPassword.submit")}
          </Button>
        </form>
        <p className="text-muted text-xs text-center">{t("resetPassword.revokesAllSessions")}</p>
      </Card>
    </div>
  );
}

export const Route = createFileRoute("/reset-password")({
  component: ResetPassword,
  validateSearch: (s: Record<string, unknown>) => ({
    token: typeof s.token === "string" ? s.token : undefined,
  }),
});
